import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Building2,
  Calendar,
  FileText,
  Send,
  CheckCircle,
  AlertTriangle,
  Pencil,
  Trash2,
  Ban,
  DollarSign,
  RotateCcw,
} from 'lucide-react';
import { useState } from 'react';
import { fetchInvoice, updateInvoiceStatus } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from '@/components/ui/table';
import { formatCurrency, formatDate, capitalizeFirst } from '@/lib/utils';
import { getInvoiceStatusBadgeVariant, getAllowedTransitions } from '@/lib/invoice-utils';
import { VoidInvoiceDialog } from '@/components/invoices/VoidInvoiceDialog';
import { RecordPaymentDialog } from '@/components/invoices/RecordPaymentDialog';
import { PaymentHistory } from '@/components/invoices/PaymentHistory';
import { CreditNoteDialog } from '@/components/invoices/CreditNoteDialog';
import { SendInvoiceDialog } from '@/components/invoices/SendInvoiceDialog';
import { InvoicePdfButton } from '@/components/invoices/InvoicePdfButton';
import { RecurringScheduleCard } from '@/components/invoices/RecurringScheduleCard';
import { DeleteInvoiceDialog } from '@/components/invoices/DeleteInvoiceDialog';
import { InvoiceForm } from '@/components/invoices/InvoiceForm';


function InvoiceDetailSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Skeleton className="h-8 w-8 rounded" />
        <Skeleton className="h-6 w-32" />
      </div>
      <div className="bg-bg-tertiary rounded-xl border border-white/10 shadow-raised surface-sheen p-6">
        <div className="space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-5 w-48" />
        </div>
      </div>
      <Skeleton className="h-64" />
      <Skeleton className="h-32" />
    </div>
  );
}

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const {
    data: invoice,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => fetchInvoice(id!),
    enabled: !!id,
  });

  const statusMutation = useMutation({
    mutationFn: (status: string) => updateInvoiceStatus(id!, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });

  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showVoidDialog, setShowVoidDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showCreditNoteDialog, setShowCreditNoteDialog] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);

  const allowedTransitions = invoice ? getAllowedTransitions(invoice.status) : [];

  if (isLoading) {
    return <InvoiceDetailSkeleton />;
  }

  if (error || !invoice) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-text-primary">Invoice Not Found</h2>
          <p className="text-text-tertiary mt-2">
            The invoice you are looking for does not exist or has been removed.
          </p>
          <Button asChild className="mt-4">
            <Link to="/invoices">Back to Invoices</Link>
          </Button>
        </div>
      </div>
    );
  }

  const canMarkAsSent = allowedTransitions.includes('sent');
  const canMarkAsPaid = allowedTransitions.includes('paid');
  const canMarkAsOverdue = allowedTransitions.includes('overdue');
  const canVoid = allowedTransitions.includes('void');
  const canEdit = invoice.status === 'draft';
  const canDelete = invoice.status === 'draft';

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <div>
        <Link
          to="/invoices"
          className="inline-flex items-center gap-2 text-sm text-text-tertiary hover:text-text-primary"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Invoices
        </Link>
      </div>

      {/* Invoice Header */}
      <div className="bg-bg-tertiary rounded-xl border border-white/10 shadow-raised surface-sheen p-6">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          <div className="flex-1">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
                <FileText className="h-7 w-7 text-emerald-500" />
                Invoice {invoice.invoiceNumber}
              </h1>
              <Badge variant={getInvoiceStatusBadgeVariant(invoice.status)}>
                {capitalizeFirst(invoice.status)}
              </Badge>
            </div>

            {invoice.voidReason && (
              <div className="mt-2 text-sm text-yellow-400 bg-yellow-400/10 border border-yellow-400/20 rounded-lg px-3 py-2">
                <span className="font-medium">Void reason:</span> {invoice.voidReason}
              </div>
            )}

            <div className="flex flex-wrap gap-4 mt-4 text-sm text-text-tertiary">
              <div className="flex items-center gap-1">
                <Building2 className="h-4 w-4" />
                <Link
                  to={`/companies/${invoice.companyId}`}
                  className="hover:text-emerald-500"
                >
                  {invoice.company.name}
                </Link>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                Issued: {formatDate(invoice.issueDate)}
              </div>
              {invoice.dueDate && (
                <div
                  className={`flex items-center gap-1 ${
                    invoice.status !== 'paid' && new Date(invoice.dueDate) < new Date()
                      ? 'text-red-500'
                      : ''
                  }`}
                >
                  <Calendar className="h-4 w-4" />
                  Due: {formatDate(invoice.dueDate)}
                  {invoice.status !== 'paid' && new Date(invoice.dueDate) < new Date() && ' (Overdue)'}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            {canEdit && (
              <Button
                variant="outline"
                onClick={() => setShowEditDialog(true)}
              >
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </Button>
            )}
            {canMarkAsSent && (
              <Button
                variant="outline"
                onClick={() => statusMutation.mutate('sent')}
                disabled={statusMutation.isPending}
              >
                <Send className="h-4 w-4 mr-2" />
                {statusMutation.isPending ? 'Updating...' : 'Mark as Sent'}
              </Button>
            )}
            {canMarkAsOverdue && (
              <Button
                variant="destructive"
                onClick={() => statusMutation.mutate('overdue')}
                disabled={statusMutation.isPending}
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                {statusMutation.isPending ? 'Updating...' : 'Mark as Overdue'}
              </Button>
            )}
            {canMarkAsPaid && (
              <Button
                onClick={() => statusMutation.mutate('paid')}
                disabled={statusMutation.isPending}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                {statusMutation.isPending ? 'Updating...' : 'Mark as Paid'}
              </Button>
            )}
            {['sent', 'partially_paid', 'overdue'].includes(invoice.status) && (
              <Button
                variant="outline"
                onClick={() => setShowPaymentDialog(true)}
              >
                <DollarSign className="h-4 w-4 mr-2" />
                Record Payment
              </Button>
            )}
            {invoice.status === 'draft' && (
              <Button
                variant="outline"
                onClick={() => setShowSendDialog(true)}
              >
                <Send className="h-4 w-4 mr-2" />
                Send Invoice
              </Button>
            )}
            <InvoicePdfButton invoiceId={invoice.id} invoiceNumber={invoice.invoiceNumber} />
            {['sent', 'partially_paid', 'overdue', 'paid'].includes(invoice.status) && (
              <Button
                variant="outline"
                onClick={() => setShowCreditNoteDialog(true)}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Issue Credit Note
              </Button>
            )}
            {canVoid && (
              <Button
                variant="outline"
                className="text-yellow-400 border-yellow-400/30 hover:bg-yellow-400/10"
                onClick={() => setShowVoidDialog(true)}
              >
                <Ban className="h-4 w-4 mr-2" />
                Void
              </Button>
            )}
            {canDelete && (
              <Button
                variant="destructive"
                onClick={() => setShowDeleteDialog(true)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Overdue Warning Banner */}
      {invoice.status === 'sent' && invoice.dueDate && new Date(invoice.dueDate) < new Date() && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <div>
            <p className="text-red-500 font-medium">This invoice is past due</p>
            <p className="text-red-400 text-sm">
              Due date was {invoice.dueDate ? formatDate(invoice.dueDate) : 'N/A'}. Consider marking it as overdue or following up with the client.
            </p>
          </div>
        </div>
      )}

      {/* Invoice Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Line Items */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Line Items</CardTitle>
            </CardHeader>
            <CardContent>
              {invoice.lineItems && invoice.lineItems.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Description</TableHead>
                      <TableHead className="w-[100px] text-right">Qty</TableHead>
                      <TableHead className="w-[140px] text-right">Unit Price</TableHead>
                      <TableHead className="w-[140px] text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoice.lineItems.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.description}</TableCell>
                        <TableCell className="text-right">{item.quantity}</TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(item.unitPrice, invoice.currency)}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(item.total, invoice.currency)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell colSpan={3} className="text-right font-medium">
                        Subtotal
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(invoice.subtotal, invoice.currency)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={3} className="text-right font-medium">
                        VAT (15%)
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(invoice.taxAmount, invoice.currency)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={3} className="text-right font-semibold text-lg">
                        Total
                      </TableCell>
                      <TableCell className="text-right font-semibold text-lg text-emerald-500">
                        {formatCurrency(invoice.totalAmount, invoice.currency)}
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              ) : (
                <div className="flex flex-col items-center justify-center h-24 text-text-tertiary">
                  <p>No line items</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          {invoice.notes && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-text-secondary whitespace-pre-wrap">{invoice.notes}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Summary */}
        <div className="space-y-6">
          {/* Invoice Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="space-y-4">
                <div>
                  <dt className="text-sm font-medium text-text-tertiary">Invoice Number</dt>
                  <dd className="text-text-primary mt-1 font-mono">{invoice.invoiceNumber}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-text-tertiary">Company</dt>
                  <dd className="text-text-primary mt-1">
                    <Link
                      to={`/companies/${invoice.companyId}`}
                      className="hover:text-emerald-500"
                    >
                      {invoice.company.name}
                    </Link>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-text-tertiary">Issue Date</dt>
                  <dd className="text-text-primary mt-1">{formatDate(invoice.issueDate)}</dd>
                </div>
                {invoice.dueDate && (
                  <div>
                    <dt className="text-sm font-medium text-text-tertiary">Due Date</dt>
                    <dd
                      className={`mt-1 ${
                        invoice.status !== 'paid' && new Date(invoice.dueDate) < new Date()
                          ? 'text-red-500 font-medium'
                          : 'text-text-primary'
                      }`}
                    >
                      {formatDate(invoice.dueDate)}
                    </dd>
                  </div>
                )}
                <div className="pt-4 border-t border-white/10">
                  <dt className="text-sm font-medium text-text-tertiary">Total Amount</dt>
                  <dd className="text-2xl font-bold text-emerald-500 mt-1">
                    {formatCurrency(invoice.totalAmount, invoice.currency)}
                  </dd>
                </div>
              </dl>
            </CardContent>
          </Card>

          {/* Related Placement */}
          {invoice.placementId && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Related Placement</CardTitle>
              </CardHeader>
              <CardContent>
                <Button variant="outline" className="w-full" asChild>
                  <Link to={`/placements/${invoice.placementId}`}>
                    View Placement
                  </Link>
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="space-y-4">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-text-tertiary" />
                  <div>
                    <dt className="text-sm font-medium text-text-tertiary">Created</dt>
                    <dd className="text-text-primary">{formatDate(invoice.createdAt)}</dd>
                  </div>
                </div>
              </dl>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Error Display */}
      {statusMutation.isError && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-500 text-sm">
          {(statusMutation.error as Error)?.message || 'Failed to update invoice status'}
        </div>
      )}

      {/* Payment History */}
      {invoice.payments && invoice.payments.length > 0 && (
        <PaymentHistory
          payments={invoice.payments}
          invoiceId={invoice.id}
          invoiceStatus={invoice.status}
        />
      )}

      {/* Balance Summary */}
      {invoice.amountPaid > 0 && (
        <div className="bg-bg-tertiary rounded-xl border border-white/10 shadow-raised surface-sheen p-4">
          <div className="flex justify-between text-sm">
            <span className="text-text-tertiary">Amount Paid:</span>
            <span className="text-emerald-400 font-medium">{formatCurrency(invoice.amountPaid, invoice.currency)}</span>
          </div>
          <div className="flex justify-between text-sm mt-1">
            <span className="text-text-tertiary">Balance Due:</span>
            <span className={`font-medium ${invoice.totalAmount - invoice.amountPaid > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
              {formatCurrency(invoice.totalAmount - invoice.amountPaid, invoice.currency)}
            </span>
          </div>
        </div>
      )}

      {/* Credit Notes */}
      {invoice.creditNotes && invoice.creditNotes.length > 0 && (
        <div className="bg-bg-tertiary rounded-xl border border-white/10 shadow-raised surface-sheen p-4">
          <h3 className="text-sm font-medium text-text-primary mb-2">Credit Notes</h3>
          {invoice.creditNotes.map((cn) => (
            <div key={cn.id} className="flex justify-between text-sm py-1">
              <span className="text-text-secondary">{cn.invoiceNumber}</span>
              <span className="text-red-400">-{formatCurrency(cn.totalAmount, invoice.currency)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Recurring Schedule */}
      <RecurringScheduleCard
        invoiceId={invoice.id}
        recurringConfig={invoice.recurringConfig}
        onUpdate={() => queryClient.invalidateQueries({ queryKey: ['invoice', id] })}
      />

      {/* Dialogs */}
      <InvoiceForm
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        onSuccess={() => setShowEditDialog(false)}
        editInvoice={invoice}
      />
      <VoidInvoiceDialog
        open={showVoidDialog}
        onOpenChange={setShowVoidDialog}
        invoiceId={invoice.id}
        invoiceNumber={invoice.invoiceNumber}
        onSuccess={() => setShowVoidDialog(false)}
      />
      <DeleteInvoiceDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        invoiceId={invoice.id}
        invoiceNumber={invoice.invoiceNumber}
      />
      <RecordPaymentDialog
        open={showPaymentDialog}
        onOpenChange={setShowPaymentDialog}
        invoiceId={invoice.id}
        invoiceNumber={invoice.invoiceNumber}
        totalAmount={invoice.totalAmount}
        amountPaid={invoice.amountPaid}
        onSuccess={() => setShowPaymentDialog(false)}
      />
      <CreditNoteDialog
        open={showCreditNoteDialog}
        onOpenChange={setShowCreditNoteDialog}
        parentInvoice={invoice}
        onSuccess={() => setShowCreditNoteDialog(false)}
      />
      <SendInvoiceDialog
        open={showSendDialog}
        onOpenChange={setShowSendDialog}
        invoiceId={invoice.id}
        invoiceNumber={invoice.invoiceNumber}
        totalAmount={invoice.totalAmount}
        currency={invoice.currency}
        onSuccess={() => setShowSendDialog(false)}
      />
    </div>
  );
}
